import React, { useState } from 'react';
import { ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { BaseComponentProps } from '../types/componentThemes';

export interface NavItem {
  /** Unique identifier for the nav item */
  id: string;
  /** Display label */
  label: string;
  /** Icon element */
  icon: React.ReactNode;
  /** Navigation path */
  path: string;
  /** Optional badge count for notifications */
  badge?: number;
  /** Optional nested navigation items */
  children?: NavItem[];
  /** Open in a new browser tab instead of navigating in-app */
  newTab?: boolean;
}

export interface AdminNavigationProps extends BaseComponentProps {
  /** Navigation items to display */
  items: NavItem[];
  /** Currently active path */
  activePath?: string;
  /** Whether the sidebar is collapsed */
  collapsed?: boolean;
  /** Callback when collapse state changes */
  onToggle?: () => void;
  /** Callback when a nav item is clicked */
  onNavigate?: (path: string) => void;
}

export const AdminNavigation: React.FC<AdminNavigationProps> = ({
  items,
  activePath = '',
  collapsed = false,
  onToggle,
  onNavigate,
  className = '',
  style,
  testId,
}) => {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  const toggleExpanded = (itemId: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  const handleItemClick = (item: NavItem) => {
    if (item.children && item.children.length > 0) {
      toggleExpanded(item.id);
    } else if (item.newTab) {
      window.open(item.path, '_blank');
    } else if (onNavigate) {
      onNavigate(item.path);
    }
  };

  const isItemActive = (item: NavItem): boolean => {
    if (item.path === activePath) return true;
    if (item.children) {
      return item.children.some((child) => isItemActive(child));
    }
    return false;
  };

  const renderNavItem = (item: NavItem, level: number = 0) => {
    const isActive = item.path === activePath;
    const isExpanded = expandedItems.has(item.id);
    const hasChildren = item.children && item.children.length > 0;
    const isHovered = hoveredItem === item.id;

    return (
      <div key={item.id}>
        <div
          className={`
            relative flex items-center gap-3 mx-2 px-3 py-2.5 text-sm font-medium cursor-pointer
            rounded-lg transition-all duration-200 min-h-[40px] select-none
            ${level > 0 ? 'ml-8' : ''}
            ${isActive
              ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 shadow-sm'
              : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700/50 hover:text-neutral-900 dark:hover:text-neutral-100'
            }
          `}
          onClick={() => handleItemClick(item)}
          onMouseEnter={() => setHoveredItem(item.id)}
          onMouseLeave={() => setHoveredItem(null)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleItemClick(item);
            }
          }}
          aria-current={isActive ? 'page' : undefined}
          aria-expanded={hasChildren ? isExpanded : undefined}
          data-testid={testId ? `${testId}-item-${item.id}` : undefined}
        >
          {/* Active indicator bar */}
          {isActive && (
            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-primary-600 dark:bg-primary-400 rounded-full" />
          )}

          {/* Icon */}
          <span
            className={`flex-shrink-0 w-5 h-5 ${
              collapsed ? 'mx-auto' : ''
            }`}
            aria-hidden="true"
          >
            {item.icon}
          </span>

          {/* Label and badge (hidden when collapsed) */}
          {!collapsed && (
            <>
              <span className="flex-1 truncate">{item.label}</span>

              {/* Badge */}
              {item.badge !== undefined && item.badge > 0 && (
                <span
                  className="flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold text-white bg-gradient-to-r from-error to-error-dark rounded-full shadow-sm"
                  aria-label={`${item.badge} notifications`}
                >
                  {item.badge > 99 ? '99+' : item.badge}
                </span>
              )}

              {/* Expand/collapse arrow for nested items */}
              {hasChildren && (
                <ChevronRight
                  className={`w-4 h-4 transition-transform duration-200 ${
                    isExpanded ? 'rotate-90' : ''
                  }`}
                />
              )}
            </>
          )}

          {/* Tooltip for collapsed mode */}
          {collapsed && isHovered && (
            <div className="absolute left-full ml-2 px-3 py-1.5 bg-neutral-900 dark:bg-neutral-100 text-white dark:text-neutral-900 text-sm font-medium rounded-lg shadow-lg whitespace-nowrap z-50 pointer-events-none">
              {item.label}
              {item.badge !== undefined && item.badge > 0 && (
                <span className="ml-2 px-1.5 py-0.5 text-xs bg-error rounded-full text-white dark:text-white">
                  {item.badge}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Nested items with smooth reveal */}
        {hasChildren && isExpanded && !collapsed && (
          <div
            className="mt-1 space-y-0.5"
            role="group"
            aria-label={`${item.label} submenu`}
          >
            {item.children!.map((child) => renderNavItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <nav
      className={`
        fixed left-0 top-16 bottom-0 bg-white dark:bg-neutral-800
        border-r border-neutral-200/80 dark:border-neutral-700/60
        overflow-y-auto overflow-x-hidden
        transition-all duration-300 ease-in-out
        ${collapsed ? 'w-16' : 'w-64'}
        ${className}
      `}
      style={style}
      data-testid={testId}
      aria-label="Admin navigation"
    >
      {/* Navigation items */}
      <div className="py-3 space-y-0.5">
        {items.map((item) => renderNavItem(item))}
      </div>

      {/* Toggle button */}
      {onToggle && (
        <button
          className="absolute bottom-4 right-4 p-2 min-w-[36px] min-h-[36px] text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-700/50 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
          onClick={onToggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!collapsed}
          data-testid={testId ? `${testId}-toggle` : undefined}
        >
          {collapsed ? (
            <ChevronsRight className="w-4 h-4" />
          ) : (
            <ChevronsLeft className="w-4 h-4" />
          )}
        </button>
      )}
    </nav>
  );
};
